import { useState } from "react";
import type { DurableObjectSource } from "@radar-sim/engine";

export interface InstructorConsoleProps {
  source: DurableObjectSource;
  paused: boolean;
  onTogglePause: () => void;
}

/**
 * Instructor controls overlay. Owns ship + time-scale + environment edits
 * are sent through the same `Command` channel as `LocalSimSource`; the
 * DO routes them into the canonical world.
 *
 * The "broadcast cursor" toggles a shared pointer that every student sees
 * — used to call out a target during a debrief without disrupting per-
 * student MARPA state.
 */
export function InstructorConsole(props: InstructorConsoleProps): JSX.Element {
  const [cog, setCog] = useState("90");
  const [sog, setSog] = useState("6");
  const [scale, setScale] = useState("1");
  const [seaState, setSeaState] = useState("2");
  const [visibility, setVisibility] = useState("8");
  const [cursorBearing, setCursorBearing] = useState("90");
  const [cursorRange, setCursorRange] = useState("3");
  const [cursorVisible, setCursorVisible] = useState(false);

  const send = props.source.sendCommand.bind(props.source);

  const setOwnCog = () => {
    const v = parseFloat(cog);
    if (Number.isFinite(v) && v >= 0 && v < 360) {
      void send({ kind: "set_own_cog", cog: v });
    }
  };
  const setOwnSog = () => {
    const v = parseFloat(sog);
    if (Number.isFinite(v) && v >= 0 && v <= 60) {
      void send({ kind: "set_own_sog", sog: v });
    }
  };
  const setTimeScale = () => {
    const v = parseFloat(scale);
    if (Number.isFinite(v) && v > 0) {
      void send({ kind: "set_time_scale", scale: v });
    }
  };

  const injectSeaState = () => {
    const v = parseInt(seaState, 10);
    if (!Number.isNaN(v) && v >= 0 && v <= 9) {
      void send({
        kind: "inject_event",
        timed: {
          atSimSeconds: 0.001,
          event: {
            kind: "sea_state_changes",
            toState: v as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9,
            transitionSeconds: 60,
          },
        },
      });
    }
  };

  const injectVisibility = () => {
    const v = parseFloat(visibility);
    if (Number.isFinite(v) && v > 0) {
      void send({
        kind: "inject_event",
        timed: {
          atSimSeconds: 0.001,
          event: {
            kind: "visibility_changes",
            toNm: v,
            transitionSeconds: 60,
          },
        },
      });
    }
  };

  const updateCursor = (visible: boolean) => {
    const b = parseFloat(cursorBearing);
    const r = parseFloat(cursorRange);
    if (!Number.isFinite(b) || !Number.isFinite(r)) return;
    setCursorVisible(visible);
    props.source.broadcastCursor({
      bearingDeg: ((b % 360) + 360) % 360,
      rangeNm: Math.max(0, r),
      visible,
    });
  };

  return (
    <div style={panel}>
      <h2 style={h2}>Instructor console</h2>
      <Row>
        <Field label="Own cog °T">
          <input
            value={cog}
            onChange={(e) => setCog(e.target.value)}
            style={inputStyle}
            inputMode="decimal"
          />
        </Field>
        <button onClick={setOwnCog} style={button}>
          Set
        </button>
        <Field label="Own sog kn">
          <input
            value={sog}
            onChange={(e) => setSog(e.target.value)}
            style={inputStyle}
            inputMode="decimal"
          />
        </Field>
        <button onClick={setOwnSog} style={button}>
          Set
        </button>
      </Row>
      <Row>
        <Field label="Time scale">
          <input
            value={scale}
            onChange={(e) => setScale(e.target.value)}
            style={inputStyle}
            inputMode="decimal"
          />
        </Field>
        <button onClick={setTimeScale} style={button}>
          Apply
        </button>
        <button onClick={props.onTogglePause} style={button}>
          {props.paused ? "Resume sim" : "Pause sim"}
        </button>
      </Row>
      <Row>
        <Field label="Sea state 0-9">
          <input
            value={seaState}
            onChange={(e) => setSeaState(e.target.value)}
            style={inputStyle}
            inputMode="numeric"
          />
        </Field>
        <button onClick={injectSeaState} style={button}>
          Apply
        </button>
        <Field label="Visibility nm">
          <input
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            style={inputStyle}
            inputMode="decimal"
          />
        </Field>
        <button onClick={injectVisibility} style={button}>
          Apply
        </button>
      </Row>
      <Row>
        <Field label="Cursor brg °T">
          <input
            value={cursorBearing}
            onChange={(e) => setCursorBearing(e.target.value)}
            style={inputStyle}
            inputMode="decimal"
          />
        </Field>
        <Field label="Cursor rng nm">
          <input
            value={cursorRange}
            onChange={(e) => setCursorRange(e.target.value)}
            style={inputStyle}
            inputMode="decimal"
          />
        </Field>
        <button
          onClick={() => updateCursor(!cursorVisible)}
          style={{
            ...button,
            background: cursorVisible ? "#6b412f" : "#2f5c3f",
          }}
        >
          {cursorVisible ? "Hide cursor" : "Broadcast cursor"}
        </button>
      </Row>
    </div>
  );
}

const panel: React.CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-md)",
  padding: 16,
  color: "var(--text)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const h2: React.CSSProperties = {
  fontSize: 14,
  margin: 0,
  fontWeight: 600,
  color: "var(--text)",
};

const inputStyle: React.CSSProperties = {
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border-strong)",
  borderRadius: "var(--radius-sm)",
  padding: "6px 10px",
  fontFamily: "inherit",
  fontSize: 13,
  width: 92,
};

const button: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--accent-fg)",
  border: "1px solid var(--accent)",
  borderRadius: "var(--radius-sm)",
  padding: "6px 12px",
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

function Row(props: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
      {props.children}
    </div>
  );
}

function Field(props: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 10, opacity: 0.7 }}>{props.label}</span>
      {props.children}
    </label>
  );
}
